import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Input,
  Switch,
} from '@artstorefronts/ui';
import { Search, Trash2, RotateCcw, Loader2 } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'sonner';
import lunr from 'lunr';
import { useTranslation } from 'react-i18next';

import AdminLayout from '../../components/AdminLayout';
import FiltersManagementSection from '../../components/FiltersManagementSection';
import MockFiltersCell from '../../components/MockFiltersCell';
import { ADMIN_MOCKS, MOCK_FILTERS } from '../../graphql/queries';
import { UPDATE_MOCK_MUTATION, DELETE_MOCK_MUTATION, RESTORE_MOCK_MUTATION } from '../../graphql/mutations';

interface Mock {
  id: string;
  uuid: string;
  description: string;
  scaleFactor: number | null;
  maxWidth: number | null;
  maxHeight: number | null;
  enabled: boolean;
  tags: string[];
  palette: string[];
  filters: MockFilter[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface MockFilter {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface FieldUpdateState {
  isLoading: boolean;
  mockId: string;
  field: string;
}

function AdminMocks() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [fieldUpdates, setFieldUpdates] = useState<FieldUpdateState[]>([]);
  const [search, setSearch] = useState('');
  const [searchIndex, setSearchIndex] = useState<lunr.Index | null>(null);
  const [localValues, setLocalValues] = useState<
    Record<string, { description: string; scaleFactor: string; maxWidth: string; maxHeight: string }>
  >({});

  const { data, loading, refetch } = useQuery(ADMIN_MOCKS);
  const { data: filtersData, refetch: refetchFilters } = useQuery(MOCK_FILTERS);
  const [updateMock] = useMutation(UPDATE_MOCK_MUTATION);
  const [deleteMock] = useMutation(DELETE_MOCK_MUTATION);
  const [restoreMock] = useMutation(RESTORE_MOCK_MUTATION);

  // Initialize local values when data loads
  useEffect(() => {
    if (data?.adminMocks) {
      const initialValues: Record<
        string,
        { description: string; scaleFactor: string; maxWidth: string; maxHeight: string }
      > = {};
      data.adminMocks.forEach((mock: Mock) => {
        initialValues[mock.id] = {
          description: mock.description || '',
          scaleFactor: mock.scaleFactor !== null ? mock.scaleFactor.toString() : '',
          maxWidth: mock.maxWidth !== null ? mock.maxWidth.toString() : '',
          maxHeight: mock.maxHeight !== null ? mock.maxHeight.toString() : '',
        };
      });
      setLocalValues(initialValues);
    }
  }, [data]);

  // Create search index when data loads
  useEffect(() => {
    if (data?.adminMocks) {
      const idx = lunr(function () {
        this.field('uuid');
        this.field('description');
        this.field('tags');

        data.adminMocks.forEach((mock: Mock) => {
          this.add({
            id: mock.id,
            uuid: mock.uuid || '',
            description: mock.description || '',
            tags: mock.tags?.join(' ') || '',
          });
        });
      });
      setSearchIndex(idx);
    }
  }, [data]);

  const setFieldLoading = useCallback((mockId: string, field: string, isLoading: boolean) => {
    setFieldUpdates((prev) => {
      if (isLoading) {
        return [...prev, { mockId, field, isLoading }];
      } else {
        return prev.filter((item) => !(item.mockId === mockId && item.field === field));
      }
    });
  }, []);

  const isFieldLoading = useCallback(
    (mockId: string, field: string) => {
      return fieldUpdates.some((item) => item.mockId === mockId && item.field === field);
    },
    [fieldUpdates]
  );

  const handleFieldUpdate = useCallback(
    async (mockId: string, field: string, value: any) => {
      setFieldLoading(mockId, field, true);

      try {
        await updateMock({
          variables: {
            input: {
              id: mockId,
              [field]: value,
            },
          },
        });

        await refetch();
      } catch (error) {
        console.error(`Error updating ${field}:`, error);
        toast.error(t('mocks.toast.update-field-failed', { field }));
      } finally {
        setFieldLoading(mockId, field, false);
      }
    },
    [updateMock, refetch, setFieldLoading, t]
  );

  const handleDescriptionChange = useCallback((mockId: string, value: string) => {
    setLocalValues((prev) => ({
      ...prev,
      [mockId]: {
        ...prev[mockId],
        description: value,
      },
    }));
  }, []);

  const handleScaleFactorChange = useCallback((mockId: string, value: string) => {
    setLocalValues((prev) => ({
      ...prev,
      [mockId]: {
        ...prev[mockId],
        scaleFactor: value,
      },
    }));
  }, []);

  const handleMaxWidthChange = useCallback((mockId: string, value: string) => {
    setLocalValues((prev) => ({
      ...prev,
      [mockId]: {
        ...prev[mockId],
        maxWidth: value,
      },
    }));
  }, []);

  const handleMaxHeightChange = useCallback((mockId: string, value: string) => {
    setLocalValues((prev) => ({
      ...prev,
      [mockId]: {
        ...prev[mockId],
        maxHeight: value,
      },
    }));
  }, []);

  const handleDescriptionUpdate = useCallback(
    async (mockId: string, value: string) => {
      await handleFieldUpdate(mockId, 'description', value || null);
    },
    [handleFieldUpdate]
  );

  const handleScaleFactorUpdate = useCallback(
    async (mockId: string, value: string) => {
      const numValue = value ? parseFloat(value) : null;
      if (value && (isNaN(numValue!) || numValue! <= 0)) {
        toast.error(t('mocks.toast.scale-factor-invalid'));
        return;
      }
      await handleFieldUpdate(mockId, 'scaleFactor', numValue);
    },
    [handleFieldUpdate, t]
  );

  const handleMaxWidthUpdate = useCallback(
    async (mockId: string, value: string) => {
      const numValue = value ? parseFloat(value) : null;
      if (value && (isNaN(numValue!) || numValue! <= 0)) {
        toast.error(t('mocks.toast.max-width-invalid'));
        return;
      }
      await handleFieldUpdate(mockId, 'maxWidth', numValue);
    },
    [handleFieldUpdate, t]
  );

  const handleMaxHeightUpdate = useCallback(
    async (mockId: string, value: string) => {
      const numValue = value ? parseFloat(value) : null;
      if (value && (isNaN(numValue!) || numValue! <= 0)) {
        toast.error(t('mocks.toast.max-height-invalid'));
        return;
      }
      await handleFieldUpdate(mockId, 'maxHeight', numValue);
    },
    [handleFieldUpdate, t]
  );

  const handleEnabledUpdate = useCallback(
    async (mockId: string, enabled: boolean) => {
      await handleFieldUpdate(mockId, 'enabled', enabled);
    },
    [handleFieldUpdate]
  );

  const handleDelete = useCallback(
    async (mockId: string) => {
      setFieldLoading(mockId, 'delete', true);

      try {
        await deleteMock({
          variables: {
            input: { id: mockId },
          },
        });

        toast.success(t('mocks.toast.delete-success'));
        await refetch();
      } catch (error) {
        console.error('Error deleting mock:', error);
        toast.error(t('mocks.toast.delete-failed'));
      } finally {
        setFieldLoading(mockId, 'delete', false);
      }
    },
    [deleteMock, refetch, setFieldLoading, t]
  );

  const handleRestore = useCallback(
    async (mockId: string) => {
      setFieldLoading(mockId, 'restore', true);

      try {
        await restoreMock({
          variables: {
            input: { id: mockId },
          },
        });

        toast.success(t('mocks.toast.restore-success'));
        await refetch();
      } catch (error) {
        console.error('Error restoring mock:', error);
        toast.error(t('mocks.toast.restore-failed'));
      } finally {
        setFieldLoading(mockId, 'restore', false);
      }
    },
    [restoreMock, refetch, setFieldLoading, t]
  );

  // Filter mocks based on search (backend already handles ordering)
  const filteredAndSortedMocks = useMemo(() => {
    if (!data?.adminMocks) return [];

    let mocks = [...data.adminMocks];

    // Apply search filter
    if (search.trim() && searchIndex) {
      try {
        const searchTerm = search
          .trim()
          .split(' ')
          .map((term) => `${term}*`)
          .join(' ');

        const results = searchIndex.search(searchTerm);
        const searchResultIds = new Set(results.map((result) => result.ref));
        mocks = mocks.filter((mock) => searchResultIds.has(mock.id));
      } catch (e) {
        console.error(e);
      }
    }

    return mocks;
  }, [data?.adminMocks, search, searchIndex]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  if (loading) {
    return (
      <AdminLayout title={t('mocks.page-title')} description={t('mocks.page-description')}>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t('mocks.loading')}</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={t('mocks.page-title')} description={t('mocks.page-description')}>
      <>
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm">
            <strong>{t('mocks.help.scale-factor-label')}</strong> {t('mocks.help.scale-factor-body')}
          </p>
          <p className="text-sm mt-2">
            <strong>{t('mocks.help.max-dimensions-label')}</strong> {t('mocks.help.max-dimensions-body')}
          </p>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="relative w-[300px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('mocks.search-placeholder')}
              className="pl-8"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {t('mocks.record-count', { count: filteredAndSortedMocks.length })}
          </div>
        </div>

        <FiltersManagementSection filtersData={filtersData} refetchFilters={refetchFilters} />

        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-72">{t('mocks.table.mock-info')}</TableHead>
                <TableHead className="w-48">{t('mocks.table.dimensions')}</TableHead>
                <TableHead className="w-32">{t('mocks.table.tags')}</TableHead>
                <TableHead className="w-32">{t('mocks.table.filters')}</TableHead>
                <TableHead className="w-20 text-right">{t('mocks.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedMocks.map((mock: Mock) => {
                const isDeleted = !!mock.deletedAt;
                const rowClasses = isDeleted ? 'opacity-50 bg-gray-50' : 'hover:bg-gray-50';

                return (
                  <TableRow key={mock.id} className={rowClasses}>
                    <TableCell>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">{t('mocks.label-uuid')}</div>
                            <div className="font-mono text-sm">
                              {mock.uuid}
                              {isDeleted && (
                                <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                  {t('mocks.badge-deleted')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">{t('mocks.label-status')}</div>
                            <div className="flex items-center gap-2">
                              {isFieldLoading(mock.id, 'enabled') ? (
                                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                              ) : (
                                <Switch
                                  checked={mock.enabled}
                                  onCheckedChange={(checked) => handleEnabledUpdate(mock.id, checked)}
                                  disabled={isDeleted}
                                />
                              )}

                              <div className="flex items-center gap-1 text-sm">
                                {mock.enabled ? (
                                  <>
                                    <span>{tc('enabled')}</span>
                                  </>
                                ) : (
                                  <>
                                    <span>{tc('disabled')}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="relative">
                          <div className="text-xs text-muted-foreground mb-1">{t('mocks.label-description')}</div>
                          <Input
                            value={localValues[mock.id]?.description ?? ''}
                            onChange={(e) => handleDescriptionChange(mock.id, e.target.value)}
                            onBlur={(e) => handleDescriptionUpdate(mock.id, e.target.value)}
                            placeholder={t('mocks.description-placeholder')}
                            className="w-full"
                            disabled={isDeleted}
                          />
                          {isFieldLoading(mock.id, 'description') && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded">
                              <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="text-xs text-muted-foreground mb-1">{t('mocks.label-updated')}</div>
                          <div className="text-sm text-zinc-500">{new Date(mock.updatedAt).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="space-y-2">
                        <div className="relative">
                          <div className="text-xs text-muted-foreground mb-1">{t('mocks.label-scale-factor')}</div>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={localValues[mock.id]?.scaleFactor ?? ''}
                            onChange={(e) => handleScaleFactorChange(mock.id, e.target.value)}
                            onBlur={(e) => handleScaleFactorUpdate(mock.id, e.target.value)}
                            placeholder={t('mocks.placeholder-default')}
                            className="w-full"
                            disabled={isDeleted}
                          />
                          {isFieldLoading(mock.id, 'scaleFactor') && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded">
                              <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                            </div>
                          )}
                        </div>

                        <div className="relative">
                          <div className="text-xs text-muted-foreground mb-1">{t('mocks.label-max-width')}</div>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={localValues[mock.id]?.maxWidth ?? ''}
                            onChange={(e) => handleMaxWidthChange(mock.id, e.target.value)}
                            onBlur={(e) => handleMaxWidthUpdate(mock.id, e.target.value)}
                            placeholder={t('mocks.placeholder-default')}
                            className="w-full"
                            disabled={isDeleted}
                          />
                          {isFieldLoading(mock.id, 'maxWidth') && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded">
                              <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                            </div>
                          )}
                        </div>

                        <div className="relative">
                          <div className="text-xs text-muted-foreground mb-1">{t('mocks.label-max-height')}</div>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={localValues[mock.id]?.maxHeight ?? ''}
                            onChange={(e) => handleMaxHeightChange(mock.id, e.target.value)}
                            onBlur={(e) => handleMaxHeightUpdate(mock.id, e.target.value)}
                            placeholder={t('mocks.placeholder-default')}
                            className="w-full"
                            disabled={isDeleted}
                          />
                          {isFieldLoading(mock.id, 'maxHeight') && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded">
                              <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {mock.tags?.map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-zinc-100 text-zinc-700 text-sm rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </TableCell>

                    <TableCell>
                      <MockFiltersCell
                        mockId={mock.id}
                        mockFilters={mock.filters}
                        availableFilters={filtersData?.mockFilters || []}
                        isDeleted={isDeleted}
                        onRefetch={refetch}
                      />
                    </TableCell>

                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {isDeleted ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRestore(mock.id)}
                            disabled={isFieldLoading(mock.id, 'restore')}
                            className="text-green-600 hover:text-green-700"
                          >
                            {isFieldLoading(mock.id, 'restore') ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RotateCcw className="w-4 h-4" />
                            )}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(mock.id)}
                            disabled={isFieldLoading(mock.id, 'delete')}
                            className="text-red-600 hover:text-red-700"
                          >
                            {isFieldLoading(mock.id, 'delete') ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </>
    </AdminLayout>
  );
}

export default AdminMocks;
